import { appendVideoPlaybackReport } from './videoPlaybackReport'
import { videoPlaybackFeatureConfig } from '@/packageLearn/config/videoPlayback'

/**
 * 播放源尝试信息。
 */
export type VideoPlaybackSourceAttempt = {
    /** 尝试阶段。 */
    stage: string
    /** 目标播放源类型。 */
    sourceType: string
    /** 目标播放引擎。 */
    playbackEngine: string
    /** 当前清晰度。 */
    selectedQuality?: string
    /** 尝试发起时间。 */
    timestamp: number
}

/**
 * 播放会话上下文。
 */
export type VideoPlaybackSessionContext = {
    /** 业务模块。 */
    module: 'course' | 'train'
    /** 课程或培训 ID。 */
    courseId?: string | number
    /** 课时 ID。 */
    lessonId?: string | number
    /** 页面类型。 */
    pageType?: string
}

/**
 * 播放会话快照。
 */
export type VideoPlaybackSession = VideoPlaybackSessionContext & {
    /** 当前播放会话 ID。 */
    sessionId: string
    /** 会话开始时间。 */
    startTime: number
    /** 当前播放会话内的源尝试记录。 */
    attempts: VideoPlaybackSourceAttempt[]
    /** 当前播放会话内的回退次数。 */
    fallbackCount: number
    /** 当前会话最终播放源类型。 */
    finalSourceType?: string
    /** 当前会话最终结果。 */
    result: 'pending' | 'success' | 'fallback' | 'failure'
}

/**
 * 视频播放遥测事件。
 *
 * <p>统一封装课程/培训播放链路的结构化上报字段。
 * 当前优先复用 `uni.report`，同时保留本地持久化记录，后续若接后端埋点接口，
 * 只需要改这一层即可。</p>
 */
export type VideoPlaybackTelemetryEvent = {
    /** 当前阶段。 */
    stage: string
    /** 播放源类型。 */
    sourceType?: string
    /** 目标播放源类型。 */
    targetSourceType?: string
    /** 播放引擎。 */
    playbackEngine?: string
    /** 目标播放引擎。 */
    targetPlaybackEngine?: string
    /** 当前资源地址。 */
    activeSourceUrl?: string
    /** 兜底资源地址。 */
    fallbackVideoUrl?: string
    /** 当前清晰度。 */
    selectedQuality?: string
    /** 是否已回退到 mp4。 */
    usingFallbackMp4?: boolean
    /** 附加说明。 */
    message?: string
}

const EVENT_NAME = 'video_playback_status'
let remoteTelemetryReporter: VideoPlaybackTelemetryReporter | null = null

/**
 * 统一对外的播放遥测载荷。
 */
export type VideoPlaybackTelemetryPayload = {
    /** 当前播放会话 ID。 */
    sessionId?: string
    /** 业务模块。 */
    module?: 'course' | 'train'
    /** 课程或培训 ID。 */
    courseId?: string | number
    /** 课时 ID。 */
    lessonId?: string | number
    /** 页面类型。 */
    pageType?: string
    /** 当前阶段。 */
    stage: string
    /** 当前事件分类。 */
    eventType: string
    /** 当前播放源类型。 */
    sourceType?: string
    /** 目标播放源类型。 */
    targetSourceType?: string
    /** 当前播放引擎。 */
    playbackEngine?: string
    /** 目标播放引擎。 */
    targetPlaybackEngine?: string
    /** 当前清晰度。 */
    selectedQuality?: string
    /** 是否已回退到 mp4。 */
    usingFallbackMp4: number
    /** 已尝试的播放源次数。 */
    attemptCount: number
    /** 已回退次数。 */
    fallbackCount: number
    /** 当前播放会话最终源类型。 */
    finalSourceType: string
    /** 当前播放会话最终结果。 */
    result: string
    /** 当前播放会话累计耗时。 */
    elapsedMs: number
    /** 当前播放地址。 */
    activeSourceUrl?: string
    /** 当前兜底地址。 */
    fallbackVideoUrl?: string
    /** 附加说明。 */
    message: string
}

/**
 * 自定义播放遥测上报器。
 */
export type VideoPlaybackTelemetryReporter = (payload: VideoPlaybackTelemetryPayload) => void | Promise<void>

/**
 * 创建一条新的播放会话。
 *
 * @param context 当前播放页面上下文
 * @returns 带唯一标识的播放会话
 */
export function createVideoPlaybackSession(context: VideoPlaybackSessionContext): VideoPlaybackSession {
    return {
        ...context,
        sessionId: `${context.module}-${context.lessonId || 'unknown'}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        startTime: Date.now(),
        attempts: [],
        fallbackCount: 0,
        finalSourceType: '',
        result: 'pending'
    }
}

/**
 * 注册远端播放遥测上报器。
 *
 * @param reporter 远端上报实现；传 `null` 可清空当前实现
 */
export function setVideoPlaybackTelemetryReporter(reporter: VideoPlaybackTelemetryReporter | null) {
    remoteTelemetryReporter = reporter
}

/**
 * 将播放阶段归类成便于检索的事件类型。
 *
 * @param stage 当前阶段
 * @returns 结构化事件分类
 */
function resolveEventType(stage: string) {
    if (stage.startsWith('session-')) {
        return 'session'
    }
    if (stage.includes('error')) {
        return 'error'
    }
    if (['loading', 'manual-retry-m3u8', 'manual-switch-hd', 'skip-hls', 'auto-downgrade', 'fallback-mp4'].includes(stage)) {
        return 'source'
    }
    if (['ready', 'play', 'pause', 'ended'].includes(stage)) {
        return 'state'
    }
    if (['play-failed', 'fallback-ready', 'hls-ready'].includes(stage)) {
        return 'result'
    }
    return 'state'
}

/**
 * 记录本次播放源尝试。
 *
 * @param session 当前播放会话
 * @param event 当前播放事件
 */
function appendPlaybackAttempt(session: VideoPlaybackSession, event: VideoPlaybackTelemetryEvent) {
    const sourceType = event.targetSourceType || event.sourceType
    const playbackEngine = event.targetPlaybackEngine || event.playbackEngine
    if (!sourceType || !playbackEngine) {
        return
    }

    const previousAttempt = session.attempts[session.attempts.length - 1]
    if (
        previousAttempt
        && previousAttempt.stage === event.stage
        && previousAttempt.sourceType === sourceType
        && previousAttempt.playbackEngine === playbackEngine
        && previousAttempt.selectedQuality === event.selectedQuality
    ) {
        return
    }

    session.attempts.push({
        stage: event.stage,
        sourceType,
        playbackEngine,
        selectedQuality: event.selectedQuality,
        timestamp: Date.now()
    })
}

/**
 * 更新播放会话的最终结果。
 *
 * @param session 当前播放会话
 * @param event 当前播放事件
 */
function syncPlaybackSession(session: VideoPlaybackSession, event: VideoPlaybackTelemetryEvent) {
    if (['loading', 'manual-retry-m3u8', 'manual-switch-hd', 'skip-hls', 'auto-downgrade', 'fallback-mp4'].includes(event.stage)) {
        appendPlaybackAttempt(session, event)
    }

    if (event.stage === 'fallback-mp4') {
        session.fallbackCount += 1
        session.result = 'fallback'
    }

    if (event.stage === 'ready') {
        session.finalSourceType = event.sourceType || event.targetSourceType || session.finalSourceType
        session.result = event.usingFallbackMp4 ? 'fallback' : 'success'
    }

    if (event.stage === 'page-error' || event.stage === 'play-failed') {
        session.result = 'failure'
    }
}

/**
 * 对播放地址做脱敏，避免把临时签名参数直接写入远端埋点。
 *
 * @param url 原始播放地址
 * @returns 去除查询参数后的地址
 */
function sanitizePlaybackUrl(url?: string) {
    if (!url) {
        return ''
    }
    const [pathname] = String(url).split('?')
    return pathname
}

/**
 * 将播放事件与播放会话快照整合成统一载荷。
 *
 * @param event 当前播放事件
 * @param session 当前播放会话
 * @returns 遥测上报载荷
 */
export function createVideoPlaybackTelemetryPayload(
    event: VideoPlaybackTelemetryEvent,
    session?: VideoPlaybackSession | null
): VideoPlaybackTelemetryPayload {
    const eventType = resolveEventType(event.stage)
    const elapsedMs = session ? Date.now() - session.startTime : 0

    return {
        sessionId: session?.sessionId,
        module: session?.module,
        courseId: session?.courseId,
        lessonId: session?.lessonId,
        pageType: session?.pageType,
        stage: event.stage,
        eventType,
        sourceType: event.sourceType,
        targetSourceType: event.targetSourceType,
        playbackEngine: event.playbackEngine,
        targetPlaybackEngine: event.targetPlaybackEngine,
        selectedQuality: event.selectedQuality,
        usingFallbackMp4: event.usingFallbackMp4 ? 1 : 0,
        attemptCount: session?.attempts.length || 0,
        fallbackCount: session?.fallbackCount || 0,
        finalSourceType: session?.finalSourceType || '',
        result: session?.result || 'pending',
        elapsedMs,
        activeSourceUrl: sanitizePlaybackUrl(event.activeSourceUrl),
        fallbackVideoUrl: sanitizePlaybackUrl(event.fallbackVideoUrl),
        message: event.message || ''
    }
}

/**
 * 尝试派发远端遥测，失败不影响当前播放与页面逻辑。
 *
 * @param payload 当前播放遥测载荷
 */
function dispatchRemoteTelemetry(payload: VideoPlaybackTelemetryPayload) {
    if (!videoPlaybackFeatureConfig.enableRemoteTelemetryReporter || !remoteTelemetryReporter) {
        return
    }
    Promise.resolve()
        .then(() => remoteTelemetryReporter?.(payload))
        .catch((error) => {
            console.warn('视频播放远端遥测上报失败', error)
        })
}

/**
 * 上报视频播放遥测信息。
 *
 * <p>当前策略：
 * 1. 写入本地播放记录，便于测试复盘；
 * 2. 若运行环境支持 `uni.report`，同步发送轻量遥测事件；
 * 3. 开发环境额外输出一份日志，方便联调。</p>
 *
 * @param event 待上报的播放事件
 * @param session 当前播放会话
 */
export function reportVideoPlaybackTelemetry(event: VideoPlaybackTelemetryEvent, session?: VideoPlaybackSession | null) {
    session && syncPlaybackSession(session, event)

    const payload = createVideoPlaybackTelemetryPayload(event, session)

    appendVideoPlaybackReport({
        sessionId: session?.sessionId,
        module: session?.module || 'course',
        courseId: session?.courseId,
        lessonId: session?.lessonId,
        pageType: session?.pageType,
        stage: event.stage,
        eventType: payload.eventType,
        sourceType: event.sourceType,
        targetSourceType: event.targetSourceType,
        playbackEngine: event.playbackEngine,
        targetPlaybackEngine: event.targetPlaybackEngine,
        activeSourceUrl: payload.activeSourceUrl,
        fallbackVideoUrl: payload.fallbackVideoUrl,
        selectedQuality: event.selectedQuality,
        usingFallbackMp4: event.usingFallbackMp4,
        attemptCount: payload.attemptCount,
        fallbackCount: payload.fallbackCount,
        finalSourceType: payload.finalSourceType,
        result: payload.result,
        elapsedMs: payload.elapsedMs,
        message: event.message
    })

    try {
        if (typeof uni !== 'undefined' && typeof uni.report === 'function') {
            uni.report(EVENT_NAME, JSON.stringify(payload))
        }
    } catch (error) {
        console.warn('视频播放遥测上报失败', error)
    }

    dispatchRemoteTelemetry(payload)

    if (videoPlaybackFeatureConfig.enableVerboseTelemetry) {
        console.info('>>>>>>>> 视频播放遥测', payload)
    }
}
